import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAssistant } from './AssistantDock';
import PlatformLogo, { PLATFORM, PLATFORMS } from './PlatformLogo';

/** A line icon (lucide geometry) — the portal's own surfaces. Platforms
 *  wear their brand marks instead, so each is recognised at a glance. */
const Icon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
    strokeLinecap="round" strokeLinejoin="round" className="w-[18px] h-[18px] flex-none">
    {children}
  </svg>
);

const ICON = {
  dashboard: <Icon><rect width="7" height="9" x="3" y="3" rx="1" /><rect width="7" height="5" x="14" y="3" rx="1" /><rect width="7" height="9" x="14" y="12" rx="1" /><rect width="7" height="5" x="3" y="16" rx="1" /></Icon>,
  engagement: <Icon><path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" /></Icon>,
  templates: <Icon><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M8 13h8" /><path d="M8 17h5" /></Icon>,
  cadences: <Icon><path d="m17 2 4 4-4 4" /><path d="M3 11v-1a4 4 0 0 1 4-4h14" /><path d="m7 22-4-4 4-4" /><path d="M21 13v1a4 4 0 0 1-4 4H3" /></Icon>,
  website: <Icon><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></Icon>,
  album: <Icon><path d="M18 22H4a2 2 0 0 1-2-2V6" /><path d="m22 13-1.296-1.296a2.41 2.41 0 0 0-3.408 0L11 18" /><circle cx="12" cy="8" r="2" /><rect width="16" height="16" x="6" y="2" rx="2" /></Icon>,
  collapse: <Icon><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 3v18" /><path d="m16 15-3-3 3-3" /></Icon>,
  expand: <Icon><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 3v18" /><path d="m14 9 3 3-3 3" /></Icon>,
  assistant: <Icon><path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12Z" /><path d="M9 12h.01M12 12h.01M15 12h.01" /></Icon>,
  signOut: <Icon><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" x2="9" y1="12" y2="12" /></Icon>,
};

/** WhatsApp's own mark — our broadcast channel. */
const WhatsAppMark = () => (
  <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] flex-none" aria-label="WhatsApp" role="img">
    <path fill="#25D366" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

type NavItem = { to: string; label: string; icon: React.ReactNode; isActive: (path: string) => boolean };

const under = (to: string) => (p: string) => p.startsWith(to);

// Platforms and Owned are per-channel pages: one for each place leads come from.
const NAV: { group?: string; items: NavItem[] }[] = [
  { items: [
    { to: '/', label: 'Dashboard', icon: ICON.dashboard, isActive: (p) => p === '/' },
    { to: '/engagement', label: 'Engagement', icon: ICON.engagement, isActive: under('/engagement') },
  ] },
  { group: 'Platforms', items: PLATFORMS.map((k) => ({
    to: PLATFORM[k].to, label: PLATFORM[k].label,
    icon: <PlatformLogo platform={k} className="w-[18px] h-[18px]" />,
    isActive: under(PLATFORM[k].to),
  })) },
  { group: 'Owned', items: [
    // The broadcast list: our WhatsApp campaigns to leads we already hold.
    { to: '/whatsapp', label: 'WhatsApp', icon: <WhatsAppMark />, isActive: under('/whatsapp') },
    // The messages module (MAG-77): every template's sends, and the cadence steps that went out.
    { to: '/templates', label: 'Templates', icon: ICON.templates, isActive: under('/templates') },
    { to: '/cadences', label: 'Cadences', icon: ICON.cadences, isActive: under('/cadences') },
    { to: '/website', label: 'Website', icon: ICON.website, isActive: under('/website') },
    { to: '/album', label: 'Album', icon: ICON.album, isActive: under('/album') },
  ] },
];

/** Collapsed = icons only; remembered per browser (a viewer convenience). */
const KEY = 'sidebar-collapsed';
const readCollapsed = () => { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } };

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const assistant = useAssistant();
  const toggle = () => setCollapsed((c) => {
    try { localStorage.setItem(KEY, c ? '0' : '1'); } catch { /* private window: just don't remember */ }
    return !c;
  });

  return (
    <aside className={`${collapsed ? 'w-[68px]' : 'w-60'} flex-none h-full flex flex-col bg-cream border-r border-charcoal/10 transition-[width] duration-200`}>
      <div className={`flex items-center pt-5 pb-4 ${collapsed ? 'flex-col gap-3 px-2' : 'gap-3 pl-5 pr-3'}`}>
        <Link to="/" className="flex items-center gap-3 min-w-0" title="Campaign Portal">
          <img src="/logo.png" alt="Bhuvana Fortune Greens" className={`${collapsed ? 'h-7' : 'h-9'} w-auto`} />
          {!collapsed && <span className="font-display text-[15px] text-forest font-semibold tracking-tight leading-tight">Campaign<br />Portal</span>}
        </Link>
        <button onClick={toggle} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className={`w-8 h-8 grid place-items-center rounded-lg text-graphite hover:text-charcoal hover:bg-charcoal/5 flex-none ${collapsed ? '' : 'ml-auto'}`}>
          {collapsed ? ICON.expand : ICON.collapse}
        </button>
      </div>

      <nav className={`flex-1 pb-3 overflow-y-auto overflow-x-hidden ${collapsed ? 'px-2.5' : 'px-3'}`}>
        {NAV.map(({ group, items }) => (
          <div key={group ?? 'main'} className="space-y-0.5">
            {group && (collapsed
              ? <div className="mx-2 my-3 border-t border-charcoal/10" />
              : <div className="text-[10.5px] uppercase tracking-[0.1em] font-semibold text-graphite/70 px-3 pt-5 pb-1.5">{group}</div>
            )}
            {items.map((item) => {
              const active = item.isActive(pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                  aria-label={collapsed ? item.label : undefined}
                  className={`relative flex items-center gap-3 h-9 rounded-lg ${collapsed ? 'justify-center' : 'px-3'} text-[13.5px] transition-colors ${
                    active
                      ? 'bg-warm-white text-forest font-semibold shadow-[0_1px_2px_rgba(61,61,61,0.08)] ring-1 ring-charcoal/[0.06]'
                      : 'text-charcoal/80 font-medium hover:bg-charcoal/[0.04] hover:text-charcoal'
                  }`}
                >
                  {active && <span className={`absolute ${collapsed ? '-left-2.5' : '-left-3'} top-2 bottom-2 w-[3px] rounded-r bg-forest`} />}
                  <span className={active ? '' : 'opacity-85'}>{item.icon}</span>
                  {!collapsed && item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {assistant.available && (
        <div className={`pb-2 ${collapsed ? 'px-2.5' : 'px-3'}`}>
          <button
            onClick={assistant.toggle}
            aria-pressed={assistant.mode !== 'closed'}
            title={collapsed ? 'Assistant' : undefined}
            className={`relative w-full flex items-center gap-3 h-9 rounded-lg ${collapsed ? 'justify-center' : 'px-3'} text-[13.5px] transition-colors ${
              assistant.mode !== 'closed'
                ? 'bg-warm-white text-forest font-semibold shadow-[0_1px_2px_rgba(61,61,61,0.08)] ring-1 ring-charcoal/[0.06]'
                : 'text-forest font-semibold hover:bg-charcoal/[0.04]'
            }`}
          >
            {ICON.assistant}
            {!collapsed && 'Assistant'}
            <span
              title={assistant.online === null ? 'Connecting' : assistant.online ? (assistant.busy ? 'Engine busy' : 'Engine online') : 'Engine offline'}
              className={`${collapsed ? 'absolute top-1.5 right-1.5' : 'ml-auto'} w-2 h-2 rounded-full ${
                assistant.online === null ? 'bg-charcoal/20' : assistant.online ? (assistant.busy ? 'bg-gold' : 'bg-forest') : 'bg-brick'
              }`}
            />
          </button>
        </div>
      )}
      <div className={`py-3 border-t border-charcoal/10 flex items-center gap-2.5 ${collapsed ? 'flex-col px-2' : 'px-3'}`}
           title={collapsed ? (user?.email || '') : undefined}>
        {user?.picture
          ? <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full flex-none" />
          : <span className="w-8 h-8 rounded-full bg-forest text-warm-white grid place-items-center font-semibold text-[13px] flex-none">
              {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
            </span>}
        {!collapsed && (
          <span className="min-w-0 flex-1">
            <span className="block text-[12.5px] font-semibold truncate">{user?.name || user?.email}</span>
            {user?.name && <span className="block text-[11px] text-graphite truncate">{user.email}</span>}
          </span>
        )}
        <button
          onClick={() => { logout(); navigate('/login'); }}
          title="Sign out"
          aria-label="Sign out"
          className="w-8 h-8 grid place-items-center rounded-lg text-graphite hover:text-brick hover:bg-brick/5 flex-none"
        >
          {ICON.signOut}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
